import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { fileUrl } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { STATION_LOGO, useRadioStatus } from "@/lib/useRadioStatus";

const PlayerContext = createContext(null);
const ENV_STREAM = process.env.REACT_APP_STREAM_URL || "";

export const PlayerProvider = ({ children }) => {
  const audio = useRef(null);
  const [track, setTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const { t } = useLang();
  const { settings } = useSite();
  const STREAM_URL = settings?.stream_url || ENV_STREAM;
  const radio = useRadioStatus(STREAM_URL);

  // Phones' lock screen / notification shows the current song and artwork while the live radio plays.
  useEffect(() => {
    if (!track?.live || !("mediaSession" in navigator) || typeof window.MediaMetadata !== "function") return;
    const art = new URL(radio.artwork || STATION_LOGO, window.location.origin).href;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: radio.title || t("player.onair"),
      artist: radio.artist || "Awa In Kelet",
      album: "Awa In Kelet Radio",
      artwork: [{ src: art, sizes: "600x600" }],
    });
  }, [track?.live, radio.title, radio.artist, radio.artwork, t]);

  useEffect(() => {
    if (audio.current) audio.current.volume = volume;
  }, [volume]);

  const start = async (src, fresh = false) => {
    const el = audio.current;
    // Live streams always reconnect so listeners join "now", not a stale or dropped buffer.
    if (fresh || el.src !== src) {
      el.src = src;
      el.load();
    }
    try {
      await el.play();
      setPlaying(true);
    } catch (e) {
      setPlaying(false);
      if (e?.name !== "AbortError") toast(t("player.error"));
    }
  };

  const playLive = () => {
    if (!STREAM_URL) return;
    setTrack({ live: true, title: t("player.onair") });
    start(STREAM_URL, true);
  };

  const playSermon = (s) => {
    const src = fileUrl(s?.audio_url);
    setTrack({ live: false, title: s?.title_en, title_mh: s?.title_mh, speaker: s?.speaker, cover: fileUrl(s?.cover_url), id: s?.id });
    if (!src) {
      audio.current.pause();
      setPlaying(false);
      toast(t("audio.soon"));
      return;
    }
    start(src);
  };

  const toggle = () => {
    const el = audio.current;
    if (!el.src) {
      if (track?.live) playLive();
      else toast(t("audio.soon"));
      return;
    }
    if (playing) {
      el.pause();
      setPlaying(false);
    } else if (track?.live) playLive();
    else start(el.src);
  };

  const close = () => {
    audio.current.pause();
    setPlaying(false);
    setTrack(null);
  };

  return (
    <PlayerContext.Provider value={{ track, playing, radio, volume, setVolume, playLive, playSermon, toggle, close }}>
      {children}
      <audio
        ref={audio}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlaying={() => setPlaying(true)}
        preload="none"
      />
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext);
